import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import emailjs from '@emailjs/browser';

interface NominationModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: string;
}

interface FormData {
  nominator_name: string;
  nominator_email: string;
}

interface CompanyFormData extends FormData {
  company_name: string;
  website: string;
  achievements: string;
  impact: string;
}

interface IndividualFormData extends FormData {
  nominee_name: string;
  company: string;
  position: string;
  achievements: string;
  impact: string;
}

const individualCategories = [
  "Thought Leader of the Year",
  "Fintech Entrepreneur of 2024",
  "Lifetime Achievement Award"
];

const NominationModal: React.FC<NominationModalProps> = ({ isOpen, onClose, category }) => {
  const isIndividualCategory = individualCategories.includes(category);
  
  const [formData, setFormData] = useState<CompanyFormData | IndividualFormData>(
    isIndividualCategory ? {
      nominee_name: '',
      company: '',
      position: '',
      achievements: '',
      impact: '',
      nominator_name: '',
      nominator_email: ''
    } : {
      company_name: '',
      website: '',
      achievements: '',
      impact: '',
      nominator_name: '',
      nominator_email: ''
    }
  );

  const [status, setStatus] = useState<{ type: 'idle' | 'loading' | 'success' | 'error'; message?: string }>({ type: 'idle' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: 'loading' });

    try {
      // Send email notification
      await emailjs.send(
        'service_jna8jdw',
        'template_8ip1acy',
        {
          category,
          ...formData,
          to_email: 'symone@titc.io'
        },
        'CEm23wDYlAqPYWh5Q'
      );

      setStatus({ 
        type: 'success', 
        message: 'Nomination submitted successfully! We will review your nomination and contact you soon.' 
      });

      // Reset form
      setFormData(
        isIndividualCategory ? {
          nominee_name: '',
          company: '',
          position: '',
          achievements: '',
          impact: '',
          nominator_name: '',
          nominator_email: ''
        } : {
          company_name: '',
          website: '',
          achievements: '',
          impact: '',
          nominator_name: '',
          nominator_email: ''
        }
      );

      setTimeout(() => {
        onClose();
        setStatus({ type: 'idle' });
      }, 3000);
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: 'Failed to submit nomination. Please try again.' 
      });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
            >
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Nominate for {category}
                  </h3>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {isIndividualCategory ? (
                      // Individual nomination fields
                      <>
                        <div>
                          <label htmlFor="nominee_name" className="block text-sm font-medium text-gray-700">
                            Nominee's Name *
                          </label>
                          <input
                            type="text"
                            id="nominee_name"
                            required
                            value={(formData as IndividualFormData).nominee_name}
                            onChange={(e) => setFormData({ ...formData, nominee_name: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>

                        <div>
                          <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                            Company *
                          </label>
                          <input
                            type="text"
                            id="company"
                            required
                            value={(formData as IndividualFormData).company}
                            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>

                        <div>
                          <label htmlFor="position" className="block text-sm font-medium text-gray-700">
                            Position *
                          </label>
                          <input
                            type="text"
                            id="position"
                            required
                            value={(formData as IndividualFormData).position}
                            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>
                      </>
                    ) : (
                      // Company nomination fields
                      <>
                        <div>
                          <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                            Company Name *
                          </label>
                          <input
                            type="text"
                            id="company_name"
                            required
                            value={(formData as CompanyFormData).company_name}
                            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>

                        <div>
                          <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                            Website
                          </label>
                          <input
                            type="url"
                            id="website"
                            value={(formData as CompanyFormData).website}
                            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>
                      </>
                    )}

                    <div>
                      <label htmlFor="achievements" className="block text-sm font-medium text-gray-700">
                        Key Achievements *
                      </label>
                      <textarea
                        id="achievements"
                        required
                        rows={3}
                        value={formData.achievements}
                        onChange={(e) => setFormData({ ...formData, achievements: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder={`Describe the ${isIndividualCategory ? 'nominee\'s' : 'company\'s'} key achievements and innovations...`}
                      />
                    </div>

                    <div>
                      <label htmlFor="impact" className="block text-sm font-medium text-gray-700">
                        Industry Impact *
                      </label>
                      <textarea
                        id="impact"
                        required
                        rows={3}
                        value={formData.impact}
                        onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder={`Describe the ${isIndividualCategory ? 'nominee\'s' : 'company\'s'} impact on the industry...`}
                      />
                    </div>

                    <div>
                      <label htmlFor="nominator_name" className="block text-sm font-medium text-gray-700">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="nominator_name"
                        required
                        value={formData.nominator_name}
                        onChange={(e) => setFormData({ ...formData, nominator_name: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="nominator_email" className="block text-sm font-medium text-gray-700">
                        Your Email *
                      </label>
                      <input
                        type="email"
                        id="nominator_email"
                        required
                        value={formData.nominator_email}
                        onChange={(e) => setFormData({ ...formData, nominator_email: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>

                    {status.message && (
                      <div className={`text-sm ${
                        status.type === 'success' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {status.message}
                      </div>
                    )}

                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        disabled={status.type === 'loading'}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                      >
                        {status.type === 'loading' ? 'Submitting...' : 'Submit Nomination'}
                      </button>
                      <button
                        type="button"
                        onClick={onClose}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NominationModal;